import { useState, useRef, useEffect, useCallback } from 'react';
import { PFLProductModalProps, PFLProductModalStateProps } from '../../types/components/collections/ProductModal';
import { ApplicationState } from '../../types/state/storeTypes';
import { User } from '../../types/api/UsersTypes';
import { Dialog } from '@headlessui/react';
import {
  ShareIcon,
  DownloadIcon,
  LeftCaretIcon,
  RightCaretIcon,
  ExitIcon,
  DownChevronIcon
} from '../../modules/Icons';
import { addMessage } from '../../state/actions/messagesActions';
import { connect, useDispatch } from 'react-redux';
import AddToWishlistButton from '../wishlists/AddToWishlistButton';
import { useSession } from 'next-auth/react';
import { Tooltip } from 'react-tooltip';
import DownloadList from './DownloadList';
import { SessionState } from '../../types/state/reducers/authReducersTypes';
import {
  fixOptionTitle,
  getVideoSources,
  optionablesToSpecs
} from '../../helpers/pages/collections/collectionsHelpers';
import EmailNotVerifiedMessage from '../shared/EmailNotVerifiedMessage';
import { userIsNotConfirmed, userIsConfirmed, userNotLoggedIn } from '../../helpers/user';
import DetectOutsideClick from '../helpers/DetectOutsideClick';
import MobileSwiper from '../helpers/MobileSwiper';
import SwipeHint from '../helpers/SwipeHint';
import isMobile from '../../hooks/isMobile';
import useVideoOrientation from '../../hooks/useVideoOrientation';
import algoliaHelpers from '../../helpers/algolia';
import { useRouter } from 'next/router';
import { QUERY_PARAMS as ALGOLIA_QUERY_PARAMS } from '../../constants/algolia';

const PFLProductModal: React.FC<PFLProductModalProps> = (props: PFLProductModalProps) => {
  const {
    product,
    collection,
    closeCallback,
    showArrows,
    nextProduct,
    previousProduct,
    setSelectedProduct,
    isModal,
    setIsModal,
    user,
    authenticated,
  } = props;
  let [isOpen, setIsOpen] = useState(true);

  const dispatch = useDispatch();
  const session = useSession();
  const router = useRouter();
  const auth = session?.data as SessionState;

  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobileDevice = isMobile();
  const isVideoLandscape = useVideoOrientation(videoRef);
  const { pushClipClickEvent } = algoliaHelpers;

  const sendAlgoliaEvent = async () => {
    let position: number;
    if (router.query[ALGOLIA_QUERY_PARAMS.POSITION])
      position = parseInt(router.query[ALGOLIA_QUERY_PARAMS.POSITION] as string);
    await pushClipClickEvent(user, collection.id, router.query[ALGOLIA_QUERY_PARAMS.QUERY_ID] as string, position);
  };

  useEffect(() => {
    if (authenticated !== true && authenticated !== false) return;
    sendAlgoliaEvent();
  }, [collection, authenticated, product]);

  const specs = optionablesToSpecs(product.variants);
  const [isExpanded, setIsExpanded] = useState(false);

  const [isEndOfCollection, setIsEndOfCollection] = useState(false);
  const [showEndIndicator, setShowEndIndicator] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const displayedSpecs = isExpanded ? Object.entries(specs) : Object.entries(specs).slice(0, 4);

  if (product == null) return;

  const buttonClasses =
    'rounded-[5px] border px-5 py-1 flex align-middle items-center gap-2 text-[13px] justify-center';
  const hoverClasses = 'hover:text-white hover:border-white hover:stroke-white';

  useEffect(() => {
    if (product) {
      setIsOpen(true);
    }
    setIsEndOfCollection(!nextProduct);
  }, [product]);

  useEffect(() => {
    videoRef.current?.load();
    if (videoRef && videoRef.current) {
      videoRef.current.play();
    }
  }, [videoRef, product]);

  function handleModalKeyDown(event: any) {
    switch (event.key) {
      case 'ArrowRight':
        showArrows && nextProduct && setSelectedProduct(nextProduct);
        break;
      case 'ArrowLeft':
        showArrows && previousProduct && setSelectedProduct(previousProduct);
        break;
      default:
        break;
    }
  }

  function setNextProduct() {
    if (!showArrows || !nextProduct) return;
    setSelectedProduct(nextProduct);
  }

  function setPreviousProduct() {
    if (!showArrows || !previousProduct) return;
    setSelectedProduct(previousProduct);
  }

  const videoClass = isVideoLandscape
    ? ' w-screen md:w-full max-h-[450px] lg:max-h-[450px] xl:max-h-[550px]'
    : ' w-screen md:w-full max-h-[600px] md:h-[570px]';

  const handleSwipe = useCallback(({ deltaX }) => {
    // If trying to go next but no next product
    if (deltaX < 0 && !nextProduct) {
      setShowEndIndicator(true);
      // Hide indicator after animation
      setTimeout(() => setShowEndIndicator(false), 500);
      return;
    }
    // If trying to go previous but no previous product
    if (deltaX > 0 && !previousProduct) {
      setShowEndIndicator(true);
      setTimeout(() => setShowEndIndicator(false), 500);
      return;
    }

    if (deltaX > 0 && previousProduct) {
      setPreviousProduct();
    } else if (deltaX < 0 && nextProduct) {
      setNextProduct();
    }
  }, [previousProduct, nextProduct]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
        closeCallback();
      }}
      className="relative z-40">
      <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
      <div
        className="fixed inset-0 overflow-y-auto"
        tabIndex={0}
        onKeyDown={(e) => handleModalKeyDown(e)}>
        <div className="flex items-center justify-center min-h-full p-8">
          <Dialog.Panel
            className={`relative mx-auto w-screen md:w-11/12 lg:w-9/12 xl:w-8/12 max-w-[1000px] rounded-[5px] bg-a-dark-gray text-white mt-0`}>
            <div
              className="absolute z-40 cursor-pointer top-4 right-4"
              onClick={() => (setIsOpen(false), closeCallback())}>
              <ExitIcon />
            </div>

            <MobileSwiper onSwipe={handleSwipe}>
              <div className={`absolute top-0 right-0 h-full w-1 bg-red-500 z-[99] transition-opacity duration-300 ${showEndIndicator && !nextProduct ? 'opacity-50' : 'opacity-0'}`} />
              <div className={`absolute top-0 left-0 h-full w-1 bg-red-500 z-[99] transition-opacity duration-300 ${showEndIndicator && !previousProduct ? 'opacity-50' : 'opacity-0'}`} />

              {isMobileDevice && <SwipeHint show={!!nextProduct || !!previousProduct} onSwipe={() => {}} switchDirection={isEndOfCollection} className="absolute inset-0 z-10 flex items-center justify-center max-h-[200px] md:max-h-[340px]" />}
              <div className={`${videoClass}`}>
                <video
                  className="w-full md:w-full max-h-[600px]"
                  preload="none"
                  poster={product.video.poster}
                  ref={videoRef}
                  controls
                  loop
                  autoPlay
                  muted>
                  { getVideoSources(product).map((source, k) => <source key={'video_' + k} src={source.src} type={source.type} /> )}
                </video>
              </div>
            </MobileSwiper>
            {showArrows && (
              <div className="relative w-screen md:w-full">
                {previousProduct && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProduct(previousProduct);
                    }}
                    className="w-[160px] absolute -left-[205px] -top-[30px] hidden xl:block cursor-pointer stroke-gray-600 text-gray-600 hover:stroke-white hover:text-white z-49 bg-white bg-opacity-10 rounded-full p-4 hover:shadow-xl">
                    <LeftCaretIcon className="inline mr-2" /> Previous Clip
                  </span>
                )}
                {nextProduct && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProduct(nextProduct);
                    }}
                    className="w-[129px] absolute -top-[30px] -right-[176px] hidden xl:block cursor-pointer stroke-gray-600 text-gray-600 hover:stroke-white hover:text-white z-49 bg-white bg-opacity-10 rounded-full p-4 hover:shadow-xl">
                    Next Clip <RightCaretIcon className="inline ml-2" />
                  </span>
                )}
              </div>
            )}
            <div className="gap-8 p-6 leading-loose md:p-12 md:flex">
              <div className="md:w-7/12">
                <p className="uppercase text-a-light-gray text-[13px]">ASSET ID: {product.sku}</p>
                <h1 className="font-[Inter] text-[26px] font-semibold">{product.name}</h1>
                <p
                  className="text-[16px] text-[#F5F5F5] font-light leading-normal"
                  dangerouslySetInnerHTML={{ __html: product.description }}></p>

                <div className="border-t border-[#2C2C2C] py-3 my-6 text-gray-500 text-[14px] leading-normal">
                  <div className="flex flex-col gap-2 mt-4">
                    {displayedSpecs.map((v) => (
                      <div className="flex gap-1" key={v[0]}>
                        <strong className="capitalize text-[14px] block whitespace-nowrap">
                          {fixOptionTitle(v[0])}:
                        </strong>
                        <div className="block text-[14px] font-normal">
                          {v[1].map((row, k) => (
                            <span key={row.value}>
                              {k !== 0 && <span className="text-gray-600"> / </span>}
                              {row.value}
                              {row.disclaimers.length > 0 && (
                                <span className="text-gray-600 text-[12px]">
                                  {' '}
                                  ({row.disclaimers.join(', ')})
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={toggleExpand}
                      className={`${
                        Object.entries(specs).length <= 4 && 'hidden'
                      } flex !justify-start text-[#AEB6BA] text-[14px]`}>
                      {isExpanded ? (
                        <span>
                          Show less{' '}
                          <DownChevronIcon className="inline ml-1 stroke-[#AEB6BA] rotate-180" />
                        </span>
                      ) : (
                        <span>
                          Show more <DownChevronIcon className="inline ml-1 stroke-[#AEB6BA]" />
                        </span>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-3 lg:flex-row">
                  <AddToWishlistButton
                    type="button"
                    product={product}
                    buttonClassName={
                      buttonClasses +
                      ' ' +
                      hoverClasses +
                      ' stroke-a-light-gray text-a-light-gray border-a-light-gray w-full lg:w-auto mb-6 md:my-0'
                    }
                    setIsOpen={setIsOpen}
                  />
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(
                        `https://www.actionvfx.com/practice-footage/${collection.permalink}/${product.id}`
                      );
                      dispatch(
                        addMessage({
                          type: 'success',
                          body: 'Current URL has been copied to your clipboard.'
                        })
                      );
                    }}
                    className={
                      buttonClasses +
                      ' ' +
                      hoverClasses +
                      ' stroke-a-light-gray w-full md:w-auto mb-6 md:my-0 text-a-light-gray border-a-light-gray'
                    }>
                    <ShareIcon /> Copy URL
                  </button>
                </div>
              </div>
              <div className="md:w-5/12">
                {userIsNotConfirmed(user as { id: number; confirmed_at: null | string }) && (
                  <p className="mt-1 leading-5 text-a-light-gray">
                    * {<EmailNotVerifiedMessage />}
                  </p>
                )}
                {product.variant_downloads?.length > 0 &&
                  (userIsConfirmed(user as { id: number; confirmed_at: null | string }) ||
                    userNotLoggedIn(user as { id: number; confirmed_at: null | string })) && (
                    <>
                      <DetectOutsideClick onOutsideClick={() => setIsModal(false)}>
                        <button
                          data-tooltip-id={`downloads-${product.id}`}
                          className="my-4 rounded-[5px] px-5 flex align-middle items-center gap-2 justify-center bg-a-blue color-white border-0 w-full uppercase text-[16px] text-center font-[Eurostile] pt-2 pb-3 font-semibold"
                          onClick={() => setIsModal(true)}>
                          <DownloadIcon className="stroke-white" /> Download Asset
                        </button>
                        <Tooltip
                          clickable={true}
                          openOnClick={true}
                          id={`downloads-${product.id}`}
                          place="bottom"
                          noArrow={true}
                          offset={0}
                          opacity={1}
                          style={{
                            boxShadow: '0px 4px 30px 0px rgba(0, 0, 0, 0.25)',
                            background: '#0B1113',
                            zIndex: 50
                          }}
                          afterShow={async () => {}}
                          afterHide={() => {}}>
                          {isModal && (
                            <DownloadList
                              downloads={product.variant_downloads}
                              product={product}
                              user={user}
                              setSelectedProduct={setSelectedProduct}
                              key={product?.id || -1}
                              userSubscription={auth?.subscription}
                              collectionId={collection.id}
                            />
                          )}
                        </Tooltip>
                      </DetectOutsideClick>
                    </>
                  )}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

const mapStateToProps = (state: ApplicationState): PFLProductModalStateProps => ({
  user: state.auth.user as User,
  authenticated: state.auth.authenticated,
});
export default connect(mapStateToProps)(PFLProductModal);
